import React from 'react';
import { PropTypes } from 'prop-types';

import CarouselCursor from '../CarouselCursor';
import TooltipCursor from '../TooltipCursor';

import { CursorThemes } from '../../consts/themes';

import { Cursors } from 'modules/ui';

const { MainCursor } = Cursors;

// Prevent resetting last position of cursor on page changes
// values are negative because we do not want to show the cursor before
// user starts moving on the page
let lastX = -100;
let lastY = -100;

const CustomCursor = ({ styles, context }) => {
    const { carousel, appDevelopmentService, article, anyDarkSection } = context;

    const ref = React.useRef(null);

    React.useEffect(() => {
        const setFromEvent = e => {
            if (e) {
                lastX = e.clientX;
                lastY = e.clientY;
            }
            if (ref.current) {
                ref.current.style.transform = `translate(${lastX}px, ${lastY}px)`;
            }
        };
        setFromEvent();
        window.addEventListener('mousemove', setFromEvent);

        const setOnScroll = () => {
            if (ref.current) {
                ref.current.style.transform = `translate(${lastX}px, ${lastY}px)`;
            }
        };
        window.addEventListener('scroll', setOnScroll);

        return () => {
            window.removeEventListener('mousemove', setFromEvent);
            window.removeEventListener('scroll', setOnScroll);
        };
    }, [ref]);

    const getHoveredCursor = React.useCallback(
        () => {
            if (carousel.hovered && !carousel.titleHovered) {
                return (
                    <div ref={ref} className={styles.container}>
                        <CarouselCursor cursorDirection={carousel.direction} carouselTheme={carousel.theme} />
                    </div>
                );
            }
            if (carousel.hovered && carousel.titleHovered) {
                return (
                    <div ref={ref} className={styles.container}>
                        <TooltipCursor messageId={carousel.titleMessageId} isTestimonial />
                    </div>
                );
            }
            if (appDevelopmentService.hovered || article.hovered) {
                return (
                    <div ref={ref} className={styles.container}>
                        <TooltipCursor messageId={appDevelopmentService.tooltipMessageId || article.tooltipMessageId} />
                    </div>
                );
            }
            if (anyDarkSection.hovered) {
                return (
                    <div ref={ref} className={styles.container}>
                        <MainCursor className={styles.mainCursor} cursorTheme={CursorThemes.DARK} />
                    </div>
                );
            }
            return null;
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [anyDarkSection, appDevelopmentService, article, carousel],
    );

    const memoizedCursor = React.useMemo(getHoveredCursor, [getHoveredCursor]);
    React.useEffect(() => {
        document.documentElement.style.cursor = memoizedCursor ? 'none' : 'auto';
    }, [memoizedCursor]);

    return <>{memoizedCursor}</>;
};

CustomCursor.propTypes = {
    styles: PropTypes.shape().isRequired,
    context: PropTypes.shape().isRequired,
    // eslint-disable-next-line
    documentLeft: PropTypes.bool.isRequired,
};

export default CustomCursor;
